/* eslint-disable @typescript-eslint/no-unused-vars */
'use client';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { SignInDialog } from '@eliadmin/core/src/components/dialog/lazy-dialogs';
import { Badge } from '@eliadmin/core/src/components/ui/badge';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useDialogStore } from '@eliadmin/core/src/components/ui/dialog';
import { HEADER_CONFIG } from '@eliadmin/core/src/constants/header-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useQuery } from '@tanstack/react-query';
import Link from '@eliadmin/core/src/components/link';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';

export type HeaderFavoritesButtonProps = {
  className?: {
    root?: string;
    info?: string;
    title?: string;
    price?: string;
    badge?: string;
  };
};

export function HeaderFavoritesButton({ className }: HeaderFavoritesButtonProps) {
  const router = useRouter();

  const me = useCustomerStore(s => s.me);
  const locale = useLocaleStore(s => s.currentLocale);

  return (
    <Button
      variant={HEADER_CONFIG.headerMain.favorites.buttonVariant}
      size="icon"
      className="h-12 w-12"
      asChild
    >
      <Link
        aria-label={locale === 'sk' ? 'Obľúbené produkty' : 'Favorite products'}
        href={me ? ROUTES.FAVORITES() : ROUTES.FAVORITE_PRODUCTS()}
        onClick={e => {
          // if (me?.customer) return;
          // e.preventDefault();
          // e.stopPropagation();
          //   openDialog({
          //     id: SignInDialog.id,
          //     content: <SignInDialog />,
          //   });
          // router.push(ROUTES.FAVORITE_PRODUCTS());
        }}
        prefetch={false}
      >
        <div className="relative">
          <FavoritesBadge />
          {HEADER_CONFIG.headerMain.favorites.icon}
        </div>
      </Link>
    </Button>
  );
}

export function FavoritesBadge() {
  const { me, isInitialized } = useCustomerStore();
  const locale = useLocaleStore(s => s.currentLocale);

  const [localFavoritesCount, setLocalFavoritesCount] = useState<number>(0);

  // Query for favorite products from the server when the user is logged in
  const favoriteItems = useQuery({
    queryKey: [CacheKey.FAVORITE_PRODUCTS, locale, me?.customer.id ?? null],
    queryFn: () =>
      fetchProducts({
        locale,
        authToken: me ? me!.authToken : '',
        query: { favorite: true, results: 9999 },
      }),
    enabled: isInitialized && !!me?.authToken,
  });

  const favoriteItemsCount = me?.authToken
    ? (favoriteItems.data?.data?.length ?? 0) // Use server data if logged in
    : localFavoritesCount; // Use local storage data if not logged in

  // Handle counting favorite items in localStorage when not authenticated
  useEffect(() => {
    const getLocalFavoritesCount = () => {
      try {
        // Accessing localStorage itself can throw when browser storage is denied.
        const storedFavorites = JSON.parse(window.localStorage.getItem('favoriteProducts') || '[]');
        return Array.isArray(storedFavorites)
          ? storedFavorites.map(Number).filter(Boolean).length
          : 0;
      } catch {
        // Unavailable or malformed guest storage must not break the header.
        return 0;
      }
    };

    // Update count on component mount
    if (!me?.authToken) {
      setLocalFavoritesCount(getLocalFavoritesCount());
    }

    // Add storage event listener for changes in localStorage
    const handleStorageChange = () => {
      if (!me?.authToken) {
        setLocalFavoritesCount(getLocalFavoritesCount());
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [me?.authToken]);

  const countText =
    favoriteItemsCount > 99 ? (
      <span>
        99<sup>+</sup>
      </span>
    ) : (
      favoriteItemsCount
    );

  return (
    <Badge
      className={cn(
        'pointer-events-none absolute right-0 top-0 aspect-square h-5 w-5 -translate-y-1/2 translate-x-1/2 justify-center bg-destructive-foreground font-light text-background',
        'ea-favorites-button__badge',
      )}
      size="circle"
    >
      {countText}
    </Badge>
  );
}
